// Cross-subdomain auth storage for operator-login (Model B).
//
// Supabase defaults to `localStorage`, which is PER-ORIGIN. With operator-login
// we redirect an operator from one *.digavouch.com host to their org's host
// (e.g. app.digavouch.com → paigekares.digavouch.com) — and a per-origin store
// would drop the session, forcing a re-login on the new subdomain.
//
// Instead we persist the Supabase session in a cookie scoped to the parent
// domain (`.digavouch.com`) so every *.digavouch.com subdomain shares the same
// login. On hosts with no shared parent (localhost, *.netlify.app, Lovable
// previews) there's nothing to share, so we fall back to localStorage.
//
// The session JSON can exceed the ~4KB per-cookie limit, so values are split
// across numbered chunk cookies (`<key>.0`, `<key>.1`, …) — the same approach
// Supabase's SSR helpers use.

interface SimpleStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const CHUNK = 3000; // keep each cookie comfortably under ~4096B incl. name+attrs
const MAX_AGE = 60 * 60 * 24 * 100; // 100 days; Supabase refreshes the token itself

function sharedCookieDomain(): string | null {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname.toLowerCase();
  return host === "digavouch.com" || host.endsWith(".digavouch.com") ? ".digavouch.com" : null;
}

function readAllCookies(): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof document === "undefined" || !document.cookie) return out;
  for (const part of document.cookie.split("; ")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    out[part.slice(0, eq)] = decodeURIComponent(part.slice(eq + 1));
  }
  return out;
}

function makeCookieStorage(domain: string): SimpleStorage {
  const attrs = `Domain=${domain}; Path=/; Secure; SameSite=Lax`;

  const remove = (key: string): void => {
    const all = readAllCookies();
    for (let i = 0; all[`${key}.${i}`] !== undefined; i++) {
      document.cookie = `${key}.${i}=; ${attrs}; Max-Age=0`;
    }
    // Clear a legacy unchunked cookie too, if one exists.
    if (all[key] !== undefined) document.cookie = `${key}=; ${attrs}; Max-Age=0`;
  };

  const get = (key: string): string | null => {
    const all = readAllCookies();
    if (all[`${key}.0`] === undefined) return all[key] ?? null;
    let value = "";
    for (let i = 0; all[`${key}.${i}`] !== undefined; i++) value += all[`${key}.${i}`];
    return value;
  };

  const set = (key: string, value: string): void => {
    remove(key); // drop stale chunks (value may have shrunk)
    const n = Math.max(1, Math.ceil(value.length / CHUNK));
    for (let i = 0; i < n; i++) {
      const chunk = encodeURIComponent(value.slice(i * CHUNK, (i + 1) * CHUNK));
      document.cookie = `${key}.${i}=${chunk}; ${attrs}; Max-Age=${MAX_AGE}`;
    }
  };

  return { getItem: get, setItem: set, removeItem: remove };
}

function memoryStorage(): SimpleStorage {
  const m = new Map<string, string>();
  return {
    getItem: (k) => m.get(k) ?? null,
    setItem: (k, v) => void m.set(k, v),
    removeItem: (k) => void m.delete(k),
  };
}

function pickStorage(): SimpleStorage {
  const domain = sharedCookieDomain();
  if (domain && typeof document !== "undefined") return makeCookieStorage(domain);
  // Non-digavouch host (localhost / preview) → per-origin localStorage is fine.
  if (typeof localStorage !== "undefined") return localStorage;
  return memoryStorage();
}

export const crossSubdomainStorage: SimpleStorage = pickStorage();
